import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../common/Header';
import SideMenu from '../common/SideMenu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { bulkDeleteLots, bulkUpdateLotStatus, deleteShipLot, fetchShipLots } from '../../../utils/apiClient';

import './Lot.css';

const DEFAULT_FILTERS = {
  query: '',
  status: 'all',
  wmsStatus: 'all',
  alert: 'all',
  destination: 'all',
  dateRange: 'all',
  dateStart: '',
  dateEnd: '',
};

const resolveCardCount = (lot) => {
  if (!lot) return null;

  const directValue = lot.totalCardCount
    ?? lot.total_card_count
    ?? lot.cardCount
    ?? lot.card_count
    ?? lot.totalCards
    ?? lot.total_cards
    ?? lot.cardQuantity
    ?? lot.card_quantity;

  if (typeof directValue === 'number' && Number.isFinite(directValue)) {
    return directValue;
  }

  if (typeof directValue === 'string') {
    const parsed = Number(directValue);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (Array.isArray(lot.cards)) {
    return lot.cards.length;
  }

  if (Array.isArray(lot.shipments)) {
    const aggregated = lot.shipments.reduce((acc, shipment) => {
      if (Array.isArray(shipment.cards)) {
        return acc + shipment.cards.length;
      }
      if (typeof shipment.cardCount === 'number') {
        return acc + shipment.cardCount;
      }
      if (typeof shipment.card_count === 'number') {
        return acc + shipment.card_count;
      }
      return acc;
    }, 0);

    if (aggregated > 0) {
      return aggregated;
    }
  }

  return null;
};

const parseStatusCode = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const resolveWmsStatus = (lot) => {
  if (!lot || typeof lot !== 'object') return null;
  const candidates = [
    lot.wmsStatus,
    lot.wms_status,
    lot.wmsStatusId,
    lot.wms_status_id,
    lot.wmsStatusCode,
    lot.wms_status_code,
    lot.shipmentStatus,
    lot.shipment_status,
    lot.status_wms,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const parsed = parseStatusCode(candidates[index]);
    if (parsed !== null) {
      return String(parsed);
    }
  }

  return null;
};

const resolveDestinationCode = (lot) => {
  if (!lot || typeof lot !== 'object') return null;
  const candidate = lot.destinationCode
    ?? lot.destination_code
    ?? (typeof lot.destination === 'string' ? lot.destination : null);
  if (!candidate) return null;
  return String(candidate).trim().toLowerCase() || null;
};

const resolveLotId = (lot) => {
  if (!lot || typeof lot !== 'object') return '';
  const candidate = lot.lotId ?? lot.lot_id ?? lot.id;
  if (candidate === undefined || candidate === null) {
    return '';
  }
  return String(candidate);
};

const resolveDestinationLabel = (lot) => {
  if (!lot || typeof lot !== 'object') return '';
  const explicit = typeof lot.destination === 'string'
    ? lot.destination.trim()
    : (typeof lot.destination_label === 'string' ? lot.destination_label.trim() : '');
  if (explicit) {
    return explicit;
  }
  const code = lot.destinationCode ?? lot.destination_code;
  return code ? String(code).trim().toUpperCase() : '';
};

const resolveCreatedAt = (lot) => {
  if (!lot || typeof lot !== 'object') return '';
  const value = lot.createdAt ?? lot.created_at;
  return value ? String(value) : '';
};

const STATUS_META = {
  shipped: { tone: 'success', key: 'lot.statuses.shipped' },
  in_progress: { tone: 'info', key: 'lot.statuses.in_progress' },
  sorted: { tone: 'info', key: 'lot.statuses.sorted' },
  ready: { tone: 'accent', key: 'lot.statuses.ready' },
  issue: { tone: 'warning', key: 'lot.statuses.issue' },
  hold: { tone: 'danger', key: 'lot.statuses.hold' },
};

const WMS_STATUS_META = {
  1: { tone: 'info', key: 'lot.wmsStatus.sent' },
  3: { tone: 'success', key: 'lot.wmsStatus.confirmed' },
  7: { tone: 'success', key: 'lot.wmsStatus.confirmed' },
};

const ALERT_META = {
  normal: { tone: 'success', key: 'lot.alerts.normal' },
  notice: { tone: 'info', key: 'lot.alerts.notice' },
  warning: { tone: 'warning', key: 'lot.alerts.warning' },
};

const ENTRY_STATUS_META = {
  1: { tone: 'info', key: 'lot.entryStatus.sent' },
  7: { tone: 'success', key: 'lot.entryStatus.confirmed' },
};

const WMS_STATUS_KEYS = Object.keys(WMS_STATUS_META);
const MANAGEMENT_STATUS_KEYS = Object.keys(STATUS_META);
const ALERT_KEYS = ['normal', 'notice', 'warning'];

const DATE_RANGE_RULES = {
  last7: 7,
  last14: 14,
  last30: 30,
};

const Lot = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [lots, setLots] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(null);
  const [lotMeta, setLotMeta] = useState(null);
  const [bulkStatusPanelOpen, setBulkStatusPanelOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('ready');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const menuRef = useRef(null);
  const getLabel = useCallback((key, fallback, options) => {
    const value = t(key, options);
    return value === key ? fallback : value;
  }, [t]);

  const PAGE_SIZE = 8;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (key, value) => {
    setDraftFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDateInputChange = (key, value) => {
    const sanitized = value || '';
    setDraftFilters(prev => {
      const next = { ...prev, [key]: sanitized };
      if (key === 'dateStart' && next.dateEnd && sanitized && sanitized > next.dateEnd) {
        next.dateEnd = sanitized;
      }
      if (key === 'dateEnd' && next.dateStart && sanitized && sanitized < next.dateStart) {
        next.dateStart = sanitized;
      }
      return next;
    });
  };

  const handleSearch = () => {
    setFilters(draftFilters);
  };

  const handleKeywordKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const destinationOptions = useMemo(() => {
    const map = new Map();
    lots.forEach((lot) => {
      const code = resolveDestinationCode(lot);
      const labelCandidate = resolveDestinationLabel(lot);
      if (code && !map.has(code)) {
        map.set(code, labelCandidate || code.toUpperCase());
      }
    });
    return Array.from(map, ([code, label]) => ({ code, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }, [lots]);

  const advancedFiltersDirty = useMemo(() => (
    draftFilters.destination !== 'all'
    || draftFilters.alert !== 'all'
    || draftFilters.dateRange !== 'all'
    || Boolean(draftFilters.dateStart)
    || Boolean(draftFilters.dateEnd)
  ), [draftFilters]);

  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setSelectedLots([]);
    setFiltersExpanded(false);
  };

  const toggleFiltersExpanded = () => {
    setFiltersExpanded(prev => !prev);
  };

  const toggleSelection = (lotId) => {
    if (!lotId) return;
    setSelectedLots(prev => (
      prev.includes(lotId)
        ? prev.filter(id => id !== lotId)
        : [...prev, lotId]
    ));
  };

  const toggleSelectAll = (checked, rows) => {
    const ids = rows
      .map(row => resolveLotId(row))
      .filter(Boolean);

    if (checked) {
      setSelectedLots(prev => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedLots(prev => prev.filter(id => !ids.includes(id)));
    }
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const applyDateFilter = (dateString) => {
    const targetDate = parseDateValue(dateString);
    if (!targetDate) return true;

    const startDate = parseDateValue(filters.dateStart);
    if (startDate && targetDate < startDate) {
      return false;
    }

    const endDate = parseDateValue(filters.dateEnd);
    if (endDate) {
      const inclusiveEnd = new Date(endDate);
      inclusiveEnd.setHours(23, 59, 59, 999);
      if (targetDate > inclusiveEnd) {
        return false;
      }
    }

    if (filters.dateRange === 'all') return true;
    const limit = DATE_RANGE_RULES[filters.dateRange];
    if (!limit) return true;

    const today = new Date();
    const diff = (today - targetDate) / (1000 * 60 * 60 * 24);
    return diff <= limit;
  };

  const filteredLots = useMemo(() => {
    const queryValue = filters.query.trim().toLowerCase();
    return lots.filter(lot => {
      const lotIdentifier = resolveLotId(lot);
      const lotDestinationCode = resolveDestinationCode(lot);
      const lotAlert = lot.alert ?? 'normal';
      const destinationLabel = resolveDestinationLabel(lot);
      const matchesQuery = queryValue === ''
        || lotIdentifier.toLowerCase().includes(queryValue)
        || destinationLabel.toLowerCase().includes(queryValue);

      const matchesStatus = filters.status === 'all' || lot.status === filters.status;
      const matchesAlert = filters.alert === 'all' || lotAlert === filters.alert;
      const matchesDestination = filters.destination === 'all'
        || (lotDestinationCode && lotDestinationCode === filters.destination);
      const matchesWmsStatus = filters.wmsStatus === 'all'
        || resolveWmsStatus(lot) === filters.wmsStatus;
      const matchesDate = applyDateFilter(resolveCreatedAt(lot));

      return matchesQuery
        && matchesStatus
        && matchesAlert
        && matchesDestination
        && matchesWmsStatus
        && matchesDate;
    });
  }, [lots, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters, filteredLots.length]);

  useEffect(() => {
    setSelectedLots(prev => prev.filter(id => filteredLots.some(lot => resolveLotId(lot) === id)));
  }, [filteredLots]);

  useEffect(() => {
    if (!selectedLots.length) {
      setMenuOpen(false);
    }
  }, [selectedLots.length]);

  const lotConstraints = useMemo(() => lotMeta?.lot_constraints ?? lotMeta?.lotConstraints ?? {}, [lotMeta]);
  const bulkActionLimit = lotConstraints?.bulkActionLimit ?? 50;
  const selectionLimitExceeded = Boolean(bulkActionLimit) && selectedLots.length > bulkActionLimit;

  const guidelineNumbers = useMemo(() => {
    const coerceNumber = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    return {
      maxCardsPerLot: coerceNumber(lotConstraints?.maxCardsPerLot),
      splitWindowDays: coerceNumber(lotConstraints?.splitWindowDays),
      maxRangeSpan: coerceNumber(lotConstraints?.maxRangeSpan),
    };
  }, [lotConstraints]);

  const guidelineValuesAvailable = useMemo(
    () => Object.values(guidelineNumbers).some((value) => value !== null),
    [guidelineNumbers],
  );

  const formatGuidelineNumber = useCallback((value) => {
    if (value === null || value === undefined) return null;
    return value.toLocaleString();
  }, []);

  // 365 日以上は実質「期間制限なし」として扱う (LOT_SPLIT_WINDOW_DAYS=9999 等の大値対応)
  const isUnlimitedDays = useCallback((value) => {
    if (value === null || value === undefined) return false;
    return Number(value) >= 365;
  }, []);

  useEffect(() => {
    if (!selectedLots.length || selectionLimitExceeded) {
      setBulkStatusPanelOpen(false);
    }
  }, [selectedLots.length, selectionLimitExceeded]);

  useEffect(() => {
    if (selectionLimitExceeded) {
      setMenuOpen(false);
    }
  }, [selectionLimitExceeded]);

  const menuActions = useMemo(() => ([
    { key: 'deleteId', label: t('lot.menu.deleteId') },
    { key: 'status', label: t('lot.menu.status') },
  ]), [t]);

  const enforceBulkLimit = () => {
    if (selectionLimitExceeded) {
      alert(t('lot.menu.limitExceeded', { limit: bulkActionLimit }));
      return false;
    }
    return true;
  };

  const handleBulkDelete = async () => {
    if (!selectedLots.length) return;
    if (!enforceBulkLimit()) return;

    const confirmMessage = t('lot.menu.deleteConfirm', { count: selectedLots.length });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const successMessage = t('lot.menu.deleteSuccess', { count: selectedLots.length });
    const failureMessage = t('lot.menu.deleteError', '選択したロットの削除に失敗しました');

    setBulkProcessing(true);
    try {
      const response = await bulkDeleteLots(selectedLots);
      if (!response.ok) {
        let errorBody = null;
        if (typeof response.json === 'function') {
          try {
            errorBody = await response.json();
          } catch (err) {
            console.debug('Bulk delete parse failed', err);
          }
        }
        throw new Error(errorBody?.message ?? failureMessage);
      }

      alert(successMessage);
      setSelectedLots([]);
      await loadLots(filters);
    } catch (error) {
      console.error('Failed to bulk delete lots', error);
      alert(error.message ?? failureMessage);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkStatusSubmit = async () => {
    if (!selectedLots.length) return;
    if (!enforceBulkLimit()) return;
    if (!bulkStatusValue) {
      alert(t('lot.menu.statusRequired', 'ステータスを選択してください。'));
      return;
    }

    const statusLabel = t(STATUS_META[bulkStatusValue]?.key ?? bulkStatusValue);
    const confirmMessage = t('lot.menu.statusConfirm', { count: selectedLots.length, status: statusLabel });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const successMessage = t('lot.menu.statusSuccess', { count: selectedLots.length, status: statusLabel });
    const failureMessage = t('lot.menu.statusError', 'ステータス変更に失敗しました');

    setBulkProcessing(true);
    try {
      const response = await bulkUpdateLotStatus(selectedLots, bulkStatusValue);
      if (!response.ok) {
        let errorBody = null;
        if (typeof response.json === 'function') {
          try {
            errorBody = await response.json();
          } catch (err) {
            console.debug('Bulk status parse failed', err);
          }
        }
        throw new Error(errorBody?.message ?? failureMessage);
      }

      alert(successMessage);
      setSelectedLots([]);
      setBulkStatusPanelOpen(false);
      await loadLots(filters);
    } catch (error) {
      console.error('Failed to bulk update lot status', error);
      alert(error.message ?? failureMessage);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleMenuAction = (actionKey) => {
    if (!selectedLots.length) return;
    if (!enforceBulkLimit()) return;

    if (actionKey === 'deleteId') {
      setMenuOpen(false);
      handleBulkDelete();
      return;
    }

    if (actionKey === 'status') {
      setMenuOpen(false);
      setBulkStatusPanelOpen(true);
    }
  };

  const handleBulkStatusCancel = () => {
    setBulkStatusPanelOpen(false);
  };

  const handleEditLot = (lotId) => {
    if (!lotId) return;
    navigate(lotId);
  };

  const handleDeleteLot = async (lotId) => {
    const identifier = lotId ? String(lotId) : '';
    if (!identifier) return;

    const confirmMessage = getLabel(
      'lot.table.deleteConfirm',
      `ロット${identifier}を削除しますか？`,
      { lotId: identifier },
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const failureMessage = getLabel('lot.table.deleteError', 'ロットの削除に失敗しました');
    const successMessage = getLabel('lot.table.deleteSuccess', 'ロットを削除しました');

    setDeleteInProgress(identifier);
    try {
      const response = await deleteShipLot(identifier);
      if (!response.ok) {
        let errorBody = null;
        if (typeof response.json === 'function') {
          try {
            errorBody = await response.json();
          } catch (err) {
            console.debug('Delete lot error body parse failed', err);
          }
        }
        throw new Error(errorBody?.message ?? failureMessage);
      }

      setLots(prev => prev.filter(lot => resolveLotId(lot) !== identifier));
      setSelectedLots(prev => prev.filter(id => id !== identifier));
      alert(successMessage);
    } catch (error) {
      console.error('Failed to delete lot', error);
      alert(error.message ?? failureMessage);
    } finally {
      setDeleteInProgress(null);
    }
  };

  const pagedLots = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredLots.slice(start, start + PAGE_SIZE);
  }, [filteredLots, page]);

  const totalPages = Math.max(1, Math.ceil(filteredLots.length / PAGE_SIZE));

  const buildParams = (currentFilters) => {
    const params = new URLSearchParams();
    if (currentFilters.query) params.append('keyword', currentFilters.query);
    if (currentFilters.status !== 'all') params.append('status', currentFilters.status);
    if (currentFilters.wmsStatus !== 'all') params.append('wms_status', currentFilters.wmsStatus);
    if (currentFilters.alert !== 'all') params.append('alert', currentFilters.alert);
    if (currentFilters.destination !== 'all') params.append('destination', currentFilters.destination);
    if (currentFilters.dateRange !== 'all') params.append('date_range', currentFilters.dateRange);
    if (currentFilters.dateStart) params.append('start_date', currentFilters.dateStart);
    if (currentFilters.dateEnd) params.append('end_date', currentFilters.dateEnd);
    return params.toString();
  };

  const loadLots = useCallback(async (currentFilters, signal) => {
    setLoading(true);
    setError(null);

    try {
      const params = buildParams(currentFilters);
      const response = await fetchShipLots(params, { signal });

      if (!response.ok) {
        throw new Error('Failed to fetch lots');
      }

      const payload = await response.json();
      setLots(payload.data ?? payload);
      setLotMeta(payload.meta ?? null);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to load ship lots', err);
      setError(t('lot.fetchError'));
      setLots(prev => (prev && prev.length ? prev : []));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    loadLots(filters, controller.signal);
    return () => controller.abort();
  }, [filters, loadLots]);

  const renderBadge = (value, metaMap) => {
    if (value === undefined || value === null) return null;
    const key = typeof value === 'number' ? String(value) : String(value).trim();
    if (!key || !metaMap[key]) return null;
    const meta = metaMap[key];
    return (
      <span className={`lot-status-badge tone-${meta.tone}`}>
        {t(meta.key)}
      </span>
    );
  };

  const toggleButtonClass = (filtersExpanded || advancedFiltersDirty)
    ? 'lot-filter-toggle lot-filter-toggle--active'
    : 'lot-filter-toggle';

  return (
    <>
      <Header />
      <SideMenu />
      <div className="lot">
        <div className="lot-toolbar">
          <div>
            <p className="lot-title">{t('lot.title')}</p>
          </div>
        </div>

        {guidelineValuesAvailable && (
          <div className="lot-guidelines">
            <p className="lot-guidelines-title">
              {getLabel('lot.guidelines.title', 'ロット追加/分離ルール')}
            </p>
            <ul className="lot-guidelines-list">
              <li>
                {isUnlimitedDays(guidelineNumbers.splitWindowDays)
                  ? getLabel('lot.guidelines.autoAddNoTimeLimit', '自動追加の条件を確認してください。', {
                      cards: formatGuidelineNumber(guidelineNumbers.maxCardsPerLot) ?? '-',
                    })
                  : getLabel('lot.guidelines.autoAdd', '自動追加の条件を確認してください。', {
                      days: guidelineNumbers.splitWindowDays ?? '-',
                      cards: formatGuidelineNumber(guidelineNumbers.maxCardsPerLot) ?? '-',
                    })}
              </li>
              <li>
                {getLabel('lot.guidelines.autoSplit', '条件超過分は新しいロットに分離されます。', {
                  range: formatGuidelineNumber(guidelineNumbers.maxRangeSpan) ?? '-',
                })}
              </li>
              <li>
                {getLabel(
                  'lot.guidelines.statusRestriction',
                  `${t('lot.statuses.shipped')}や${t('lot.statuses.ready')}のロットには発送IDを追加できません。`,
                  {
                    shipped: t('lot.statuses.shipped'),
                    ready: t('lot.statuses.ready'),
                  },
                )}
              </li>
            </ul>
          </div>
        )}

        <div className="lot-guidelines">
          <p className="lot-guidelines-title">
            {getLabel('lot.wmsGuide.title', 'WMS連携について')}
          </p>
          <ul className="lot-guidelines-list">
            <li>
              {getLabel('lot.wmsGuide.autoSync', 'ロットの作成・更新・削除はWMS（倉庫管理システム）に自動同期されます。')}
            </li>
            <li>
              {getLabel('lot.wmsGuide.autoTracking', '追跡番号・物流会社コード・重量はWMSから出荷確認を受信した際に自動更新されます。手動入力は不要です。')}
            </li>
            <li>
              {getLabel('lot.wmsGuide.statusFlow', 'WMSから出荷確認を受信すると、ステータスは自動的に「発送済み」に変更され、配送IDのステータスも完了になります。')}
            </li>
            <li>
              {getLabel('lot.wmsGuide.deleteNote', 'ロットを削除するとWMSにキャンセルが送信されます。出荷済みのロットはキャンセルできません。')}
            </li>
          </ul>
        </div>

        <div className="lot-controls">
          <div className="lot-actions">
            <button type="button" className="lot-btn primary" onClick={() => navigate('new')}>
              {t('lot.actions.create')}
            </button>
            <div className="lot-menu" ref={menuRef}>
              <button
                type="button"
                className="lot-btn secondary lot-menu-toggle"
                onClick={() => setMenuOpen(prev => !prev)}
                disabled={!selectedLots.length || bulkProcessing || selectionLimitExceeded}
              >
                {t('lot.actions.more')}
              </button>
              {menuOpen && (
                <div className="lot-menu-panel">
                  {menuActions.map(action => (
                    <button
                      key={action.key}
                      type="button"
                      className="lot-menu-item"
                      onClick={() => handleMenuAction(action.key)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedLots.length > 0 && (
              <div className="lot-menu-helper-group">
                <span className="lot-menu-helper">
                  {t('lot.menu.bulkSummary', { count: selectedLots.length, limit: bulkActionLimit })}
                </span>
                {selectionLimitExceeded && (
                  <span className="lot-menu-helper lot-menu-helper--warning">
                    {t('lot.menu.limitWarning', { limit: bulkActionLimit })}
                  </span>
                )}
              </div>
            )}
          </div>
          {bulkStatusPanelOpen && (
            <div className="lot-bulk-panel">
              <div className="lot-field lot-field--bulk">
                <label>{t('lot.menu.statusLabel')}</label>
                <select value={bulkStatusValue} onChange={(e) => setBulkStatusValue(e.target.value)} disabled={bulkProcessing}>
                  <option value="">{t('lot.menu.statusPlaceholder')}</option>
                  {MANAGEMENT_STATUS_KEYS.map((statusKey) => (
                    <option key={statusKey} value={statusKey}>{t(STATUS_META[statusKey].key)}</option>
                  ))}
                </select>
                <p className="lot-bulk-panel-hint">{t('lot.menu.statusDescription')}</p>
              </div>
              <div className="lot-bulk-actions">
                <button type="button" className="lot-btn primary" onClick={handleBulkStatusSubmit} disabled={bulkProcessing}>
                  {t('lot.menu.statusApply')}
                </button>
                <button type="button" className="lot-btn ghost" onClick={handleBulkStatusCancel} disabled={bulkProcessing}>
                  {t('lot.menu.statusCancel')}
                </button>
              </div>
            </div>
          )}
          <div className="lot-filter-panel">
            <div className="lot-filter-row">
              <div className="lot-field lot-field--wide">
                <label>{t('lot.filters.keyword')}</label>
                <input
                  type="text"
                  value={draftFilters.query}
                  placeholder={t('lot.filters.placeholder')}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                />
              </div>
              <div className="lot-field">
                <label>{t('lot.table.wmsStatus')}</label>
                <select value={draftFilters.wmsStatus} onChange={(e) => handleFilterChange('wmsStatus', e.target.value)}>
                  <option value="all">{t('lot.filters.all')}</option>
                  {WMS_STATUS_KEYS.map((statusKey) => (
                    <option key={statusKey} value={statusKey}>{t(WMS_STATUS_META[statusKey].key)}</option>
                  ))}
                </select>
              </div>
              <div className="lot-field">
                <label>{t('lot.filters.managementStatus')}</label>
                <select value={draftFilters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  <option value="all">{t('lot.filters.all')}</option>
                  {MANAGEMENT_STATUS_KEYS.map((statusKey) => (
                    <option key={statusKey} value={statusKey}>{t(STATUS_META[statusKey].key)}</option>
                  ))}
                </select>
              </div>
            </div>

            {filtersExpanded && (
              <div className="lot-filter-grid lot-filter-grid--advanced">
                <div className="lot-field">
                  <label>{t('lot.filters.destination')}</label>
                  <select value={draftFilters.destination} onChange={(e) => handleFilterChange('destination', e.target.value)}>
                    <option value="all">{t('lot.filters.destinationAll')}</option>
                    {destinationOptions.map(option => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="lot-field">
                  <label>{t('lot.filters.alert')}</label>
                  <select value={draftFilters.alert} onChange={(e) => handleFilterChange('alert', e.target.value)}>
                    <option value="all">{t('lot.filters.all')}</option>
                    {ALERT_KEYS.map(alertKey => (
                      <option key={alertKey} value={alertKey}>{t(`lot.alerts.${alertKey}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="lot-field">
                  <label>{t('lot.filters.dateRange')}</label>
                  <select value={draftFilters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)}>
                    <option value="all">{t('lot.filters.dateAll')}</option>
                    <option value="last7">{t('lot.filters.last7')}</option>
                    <option value="last14">{t('lot.filters.last14')}</option>
                    <option value="last30">{t('lot.filters.last30')}</option>
                  </select>
                </div>
                <div className="lot-field">
                  <label>{t('lot.filters.dateStart')}</label>
                  <input
                    type="date"
                    value={draftFilters.dateStart}
                    max={draftFilters.dateEnd || undefined}
                    onChange={(e) => handleDateInputChange('dateStart', e.target.value)}
                  />
                </div>
                <div className="lot-field">
                  <label>{t('lot.filters.dateEnd')}</label>
                  <input
                    type="date"
                    value={draftFilters.dateEnd}
                    min={draftFilters.dateStart || undefined}
                    onChange={(e) => handleDateInputChange('dateEnd', e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="lot-filter-footer">
              <button type="button" className={toggleButtonClass} onClick={toggleFiltersExpanded}>
                {filtersExpanded ? t('lot.filters.hideAdvanced') : t('lot.filters.showAdvanced')}
              </button>
              <div className="lot-filter-actions">
                <button type="button" className="lot-btn secondary" onClick={handleSearch}>
                  {t('lot.filters.search')}
                </button>
                <button type="button" className="lot-btn ghost" onClick={handleReset}>
                  {t('lot.filters.reset')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <p className="lot-error">{error}</p>}
        {loading && <p className="lot-loading">{t('lot.loading')}</p>}

        <div className="lot-table-wrapper">
          <table className="lot-table">
            <thead>
              <tr>
                <th className="lot-select-cell">
                  <input
                    type="checkbox"
                    checked={pagedLots.length > 0 && pagedLots.every(row => {
                      const rowId = resolveLotId(row);
                      return rowId && selectedLots.includes(rowId);
                    })}
                    onChange={(e) => toggleSelectAll(e.target.checked, pagedLots)}
                    aria-label={t('lot.actions.bulkSelectToggle')}
                  />
                </th>
                <th>{t('lot.table.lotId')}</th>
                <th>{t('lot.table.shipmentCount')}</th>
                <th>{t('lot.table.destination')}</th>
                <th>{t('lot.table.totalCards')}</th>
                <th>{t('lot.table.wmsStatus')}</th>
                <th>{t('lot.table.managementStatus')}</th>
                <th>{t('lot.table.alert')}</th>
                <th>{t('lot.table.createdAt')}</th>
                <th>{t('lot.table.action')}</th>
              </tr>
            </thead>
            <tbody>
              {!pagedLots.length && (
                <tr>
                  <td colSpan="10" className="lot-empty">
                    {t('lot.empty')}
                  </td>
                </tr>
              )}
              {pagedLots.map((lot, index) => {
                const lotIdentifier = resolveLotId(lot);
                const destinationLabel = resolveDestinationLabel(lot);
                const createdAtValue = resolveCreatedAt(lot);
                const cardCount = resolveCardCount(lot);
                const rowKey = lotIdentifier || `lot-row-${index}`;
                const shipmentCount = lot.shipmentCount ?? lot.shipment_count ?? null;
                return (
                  <tr key={rowKey}>
                    <td className="lot-select-cell">
                      <input
                        type="checkbox"
                        checked={lotIdentifier ? selectedLots.includes(lotIdentifier) : false}
                        onChange={() => toggleSelection(lotIdentifier)}
                        aria-label={t('lot.actions.bulkSelectRow', { lotId: lotIdentifier || '-' })}
                      />
                    </td>
                    <td className="lot-identifier">
                      {lotIdentifier
                        ? <span className="lot-link" onClick={() => navigate(lotIdentifier)} style={{cursor:'pointer',textDecoration:'underline'}}>{lotIdentifier}</span>
                        : '-'}
                    </td>
                    <td className="lot-cell-center">{shipmentCount ?? '-'}</td>
                    <td>{destinationLabel || '-'}</td>
                    <td className="lot-cell-center">
                      {typeof cardCount === 'number' ? cardCount.toLocaleString() : '-'}
                    </td>
                    <td className="lot-cell-center">
                      <div className="lot-wms-status-row">
                        <span className="lot-wms-label">{t('lot.wmsLabels.entry')}</span>
                        {lot.entryOrderStatus == null
                          ? <span className="lot-wms-text-muted">{t('lot.entryStatus.unsent')}</span>
                          : renderBadge(lot.entryOrderStatus, ENTRY_STATUS_META)}
                      </div>
                      <div className="lot-wms-status-row">
                        <span className="lot-wms-label">{t('lot.wmsLabels.delivery')}</span>
                        {resolveWmsStatus(lot)
                          ? renderBadge(resolveWmsStatus(lot), WMS_STATUS_META)
                          : <span className="lot-wms-text-muted">{t('lot.wmsStatus.unsent')}</span>}
                      </div>
                    </td>
                    <td className="lot-cell-center">{renderBadge(lot.status, STATUS_META) || '-'}</td>
                    <td className="lot-cell-center">{renderBadge(lot.alert, ALERT_META) || '-'}</td>
                    <td className="lot-cell-date">{createdAtValue || '-'}</td>
                    <td>
                      <div className="lot-row-actions">
                        <button
                          type="button"
                          className="lot-link"
                          onClick={() => handleEditLot(lotIdentifier)}
                          disabled={!lotIdentifier}
                        >
                          {t('lot.table.edit')}
                        </button>
                        <button
                          type="button"
                          className="lot-link danger"
                          onClick={() => handleDeleteLot(lotIdentifier)}
                          disabled={!lotIdentifier || deleteInProgress === lotIdentifier}
                        >
                          {t('lot.table.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="lot-pagination">
          <button
            type="button"
            className="lot-pagination-btn"
            disabled={page === 1}
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
          >
            {t('lot.pagination.prev')}
          </button>
          <div className="lot-pagination-pages">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={`page-${index + 1}`}
                type="button"
                className={index + 1 === page ? 'lot-page active' : 'lot-page'}
                onClick={() => setPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="lot-pagination-btn"
            disabled={page === totalPages}
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
          >
            {t('lot.pagination.next')}
          </button>
        </div>
      </div>
    </>
  );
};

export default Lot;
